import { useState } from 'react';
import {
    Loader2, ChevronLeft, ChevronRight, ChevronDown, ChevronRight as ChevronRightSmall,
    RotateCw, AlertCircle, AlertTriangle, CheckCircle2, MinusCircle, X,
} from 'lucide-react';
import { useDocuWarePoChecks, PO_CHECK_PAGE_SIZE } from '../../../hooks/useDocuWarePoChecks';
import { formatDate, formatDateTimeCH } from '../../../utils/format';
import { PoCheckDetailPanel } from './PoCheckDetail';
import type { PoCheckRow, PoCheckVerdict, PoCheckSortField } from '../../../types/docuwarePoCheck';
import { findingLabel, FINDING_LABELS } from '../../../types/docuwarePoCheck';
import { COMPANY_LABELS } from '../../../types/docuwareInvoices';
import { useResizableColumns } from '../../../hooks/useResizableColumns';
import { ColumnResizeHandle } from '../../ui/ColumnResizeHandle';
import { DocuWareDocLink } from '../DocuWareDocLink';

interface Props {
    canEdit: boolean;
    onToast: (msg: string, kind: 'success' | 'error') => void;
    onCopyDocId: (docId: string) => void;
}

const VERDICT_STYLE: Record<PoCheckVerdict, { icon: typeof AlertCircle; cls: string; label: string }> = {
    Error: { icon: AlertCircle, cls: 'text-red-600', label: 'Error' },
    Warning: { icon: AlertTriangle, cls: 'text-amber-600', label: 'Warning' },
    Ok: { icon: CheckCircle2, cls: 'text-green-600', label: 'Ok' },
    Unchecked: { icon: MinusCircle, cls: 'text-gray-400', label: 'Not checked' },
};

/**
 * `id` is the stable identity for the stored column width; `sort` is what the server sorts on.
 * They cannot be the same field — two columns have no sort at all, and a null key is useless
 * as a storage key.
 */
interface PoCol {
    id: string;
    label: string;
    sort?: PoCheckSortField;
    align?: 'right';
    width: number;
}

const WIDTH_KEY = 'docuware-po-check-col-widths';

const COLS: PoCol[] = [
    { id: 'expand', label: '', width: 32 },
    { id: 'verdict', label: 'Verdict', sort: 'verdict', width: 110 },
    // Two companies in one queue, so which one a row belongs to has to be readable off the row.
    { id: 'company', label: 'Company', width: 90 },
    { id: 'supplier', label: 'Supplier', sort: 'supplier', width: 300 },
    { id: 'invoiceNumber', label: 'Invoice no.', sort: 'invoiceNumber', width: 150 },
    { id: 'invoiceDate', label: 'Invoice date', sort: 'invoiceDate', width: 110 },
    { id: 'amount', label: 'Amount', sort: 'amount', align: 'right', width: 120 },
    { id: 'poNo', label: 'Order', sort: 'poNo', width: 140 },
    { id: 'finding', label: 'Finding', width: 200 },
    { id: 'review', label: 'Review', sort: 'reviewStatus', width: 90 },
    { id: 'checked', label: 'Checked', sort: 'checked', width: 140 },
    // The way out of a finding and into the actual invoice. Without it a finding is a claim
    // the reviewer has no way to check.
    { id: 'document', label: 'Document', width: 110 },
];

export function PoCheckTab({ canEdit, onToast, onCopyDocId }: Props) {
    const {
        rows, summary, total, loading, error,
        filters, setFilters, clearFilters, activeFilterCount,
        sortBy, sortOrder, setSort,
        page, setPage, totalPages, reload,
    } = useDocuWarePoChecks(true);

    // Single-open, like the invoice table: two expanded order comparisons side by side is
    // more scrolling than reading.
    const [expandedId, setExpandedId] = useState<number | null>(null);

    const { widthOf, totalWidthOf, resizingKey, startResize, resetWidth, shouldSuppressClick } =
        useResizableColumns(COLS.map(c => ({ key: c.id, width: c.width })), WIDTH_KEY);
    const totalWidth = totalWidthOf(COLS.map(c => c.id));

    const counts = summary.verdicts;

    // What the verdicts are counted OVER. Picking a category turns the tab into that queue,
    // and the server reads every verdict off the findings inside it - so a document whose VAT
    // findings are only warnings reads Warning under "VAT" and Error with the filter off.
    // That is the intended answer, but only if the screen says which question it answered.
    const scopeLabel = filters.findingCode
        ? findingLabel(filters.findingCode)
        : summary.findingCategories.find(c => c.category === filters.findingCategory)?.label ?? null;

    return (
        <div className="space-y-4">
            {/* Verdict tiles double as the primary filter. The count is over the whole
                filtered set, not the page - "3 errors" has to mean three, full stop. */}
            <div className="flex flex-wrap gap-2">
                {(['Error', 'Warning', 'Ok', 'Unchecked'] as PoCheckVerdict[]).map(v => {
                    const style = VERDICT_STYLE[v];
                    const Icon = style.icon;
                    const active = filters.verdict === v;
                    return (
                        <button
                            key={v}
                            onClick={() => setFilters({ verdict: active ? '' : v })}
                            className={`inline-flex items-center gap-2 rounded border px-3 py-2 text-sm transition-colors ${
                                active ? 'border-blue-500 bg-blue-50' : 'border-gray-200 bg-white hover:bg-gray-50'
                            }`}
                        >
                            <Icon className={`h-4 w-4 ${style.cls}`} />
                            <span className="text-gray-700">{style.label}</span>
                            <span className="font-semibold text-gray-900">
                                {(counts[v] ?? 0).toLocaleString('de-CH')}
                            </span>
                        </button>
                    );
                })}
            </div>

            {scopeLabel && (
                <p className="-mt-2 text-xs text-gray-500">
                    Verdict, finding and count are read off the{' '}
                    <span className="font-medium text-gray-700">{scopeLabel}</span> findings only.
                    A document with a worse finding elsewhere still reads by its {scopeLabel} ones here.
                </p>
            )}

            <div className="flex flex-wrap items-end gap-3">
                <label className="block">
                    <span className="mb-1 block text-xs font-medium text-gray-600">Search</span>
                    <input
                        value={filters.search}
                        onChange={e => setFilters({ search: e.target.value })}
                        placeholder="Supplier, invoice no. or order no."
                        className="h-9 w-72 rounded border border-gray-300 px-2 text-sm"
                    />
                </label>

                <label className="block">
                    <span className="mb-1 block text-xs font-medium text-gray-600">Company</span>
                    <select
                        value={filters.companyCode}
                        onChange={e => setFilters({ companyCode: e.target.value })}
                        className="h-9 w-40 rounded border border-gray-300 px-2 text-sm"
                    >
                        <option value="">All companies</option>
                        {summary.companies.map(c => (
                            <option key={c} value={c}>{COMPANY_LABELS[c] ?? c}</option>
                        ))}
                    </select>
                </label>

                {/* Category before Finding, and Finding narrows to it. "Everything about VAT"
                    is the question people actually arrive with; which six of the 21 rules that
                    is should not be something they have to know. */}
                <label className="block">
                    <span className="mb-1 block text-xs font-medium text-gray-600">Category</span>
                    <select
                        value={filters.findingCategory}
                        onChange={e => setFilters({ findingCategory: e.target.value })}
                        className="h-9 w-52 rounded border border-gray-300 px-2 text-sm"
                    >
                        <option value="">Any category</option>
                        {summary.findingCategories.map(c => (
                            <option key={c.category} value={c.category}>
                                {c.label} ({c.docs})
                            </option>
                        ))}
                    </select>
                </label>

                <label className="block">
                    <span className="mb-1 block text-xs font-medium text-gray-600">Finding</span>
                    <select
                        value={filters.findingCode}
                        onChange={e => setFilters({ findingCode: e.target.value })}
                        className="h-9 w-64 rounded border border-gray-300 px-2 text-sm"
                    >
                        <option value="">
                            {filters.findingCategory ? 'Any finding in this category' : 'Any finding'}
                        </option>
                        {summary.findingCodes.map(f => (
                            <option key={f.code} value={f.code}>
                                {findingLabel(f.code)} ({f.docs})
                            </option>
                        ))}
                    </select>
                </label>

                <label className="block">
                    <span className="mb-1 block text-xs font-medium text-gray-600">Review status</span>
                    <select
                        value={filters.reviewStatus}
                        onChange={e => setFilters({ reviewStatus: e.target.value })}
                        className="h-9 w-44 rounded border border-gray-300 px-2 text-sm"
                    >
                        <option value="">Any status</option>
                        <option value="Open">Open</option>
                        <option value="CheckedOk">Checked, acceptable</option>
                        <option value="Disputed">Disputed</option>
                    </select>
                </label>

                <label className="block">
                    <span className="mb-1 block text-xs font-medium text-gray-600">Order</span>
                    <select
                        value={filters.poResolution}
                        onChange={e => setFilters({ poResolution: e.target.value })}
                        className="h-9 w-44 rounded border border-gray-300 px-2 text-sm"
                    >
                        <option value="">Any</option>
                        <option value="open">Open order</option>
                        <option value="posted">Posted invoice</option>
                        <option value="none">Not found</option>
                        <option value="multi">Several orders</option>
                        <option value="notapplicable">No order number</option>
                    </select>
                </label>

                {activeFilterCount > 0 && (
                    <button
                        onClick={clearFilters}
                        className="inline-flex h-9 items-center gap-1 rounded border border-gray-300 px-3 text-sm text-gray-600 hover:bg-gray-50"
                    >
                        <X className="h-4 w-4" /> Clear filters ({activeFilterCount})
                    </button>
                )}
            </div>

            <div className="flex flex-wrap items-center gap-3 text-sm text-gray-500">
                <button
                    onClick={() => void reload()}
                    disabled={loading}
                    className="inline-flex h-8 items-center gap-2 rounded border border-gray-300 px-3 text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                    title="Re-run the query against what is already checked. Documents are checked by the hourly import, or by Import last 2 days on the Invoices tab."
                >
                    <RotateCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
                    Reload table
                </button>
                <span>{total.toLocaleString('de-CH')} checked invoice{total === 1 ? '' : 's'}</span>
                <span className="text-gray-300">·</span>
                <span>
                    Last check: {summary.lastCheckedAt ? formatDateTimeCH(summary.lastCheckedAt) : 'never'}
                </span>
            </div>

            {error && (
                <div className="rounded border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
                    {error}
                </div>
            )}

            <div className="rounded-lg border border-gray-200 bg-white">
                {loading && rows.length === 0 ? (
                    <div className="flex h-48 items-center justify-center">
                        <Loader2 className="h-6 w-6 animate-spin text-blue-500" />
                    </div>
                ) : rows.length === 0 ? (
                    <div className="flex h-48 items-center justify-center px-6 text-center">
                        <p className="text-sm text-gray-500">
                            {activeFilterCount > 0
                                ? 'No checks match these filters.'
                                : 'No invoices have been checked yet. Goods invoices carrying a purchase-order number are checked by the hourly import.'}
                        </p>
                    </div>
                ) : (
                    // The scroll container the sticky header sticks inside of.
                    <div className="max-h-[70vh] overflow-auto">
                        <table className="w-full table-fixed text-sm" style={{ minWidth: totalWidth }}>
                            <colgroup>
                                {COLS.map(c => <col key={c.id} style={{ width: widthOf(c.id) }} />)}
                            </colgroup>
                            {/* Explicit background, and an inset shadow instead of border-b —
                                a border on a sticky th does not travel with it. */}
                            <thead className="sticky top-0 z-20 bg-gray-50 shadow-[inset_0_-1px_0_rgb(229,231,235)]">
                                <tr>
                                    {COLS.map((c) => (
                                        <th key={c.id}
                                            className={`relative px-3 py-2 font-medium text-gray-600 ${c.align === 'right' ? 'text-right' : 'text-left'}`}>
                                            {c.sort ? (
                                                <button
                                                    // Swallow the click that ends a resize drag,
                                                    // or every drag also re-sorts the queue.
                                                    onClick={() => { if (!shouldSuppressClick()) setSort(c.sort!); }}
                                                    className="inline-flex max-w-full items-center gap-1 hover:text-gray-900"
                                                >
                                                    <span className="truncate">{c.label}</span>
                                                    {sortBy === c.sort && (
                                                        <span className="shrink-0 text-gray-400">{sortOrder === 'asc' ? '▲' : '▼'}</span>
                                                    )}
                                                </button>
                                            ) : c.label}
                                            <ColumnResizeHandle
                                                label={c.label || c.id}
                                                active={resizingKey === c.id}
                                                onStart={(x) => startResize(c.id, x)}
                                                onReset={() => resetWidth(c.id)}
                                            />
                                        </th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {rows.map((r: PoCheckRow) => {
                                    const style = VERDICT_STYLE[r.verdict] ?? VERDICT_STYLE.Unchecked;
                                    const Icon = style.icon;
                                    const open = expandedId === r.documentId;
                                    return (
                                        <>
                                            <tr
                                                key={r.documentId}
                                                onClick={() => setExpandedId(open ? null : r.documentId)}
                                                className="cursor-pointer border-b border-gray-100 hover:bg-gray-50"
                                            >
                                                <td className="px-3 py-2 text-gray-400">
                                                    {open ? <ChevronDown className="h-4 w-4" /> : <ChevronRightSmall className="h-4 w-4" />}
                                                </td>
                                                <td className="px-3 py-2">
                                                    <span className="inline-flex items-center gap-1.5">
                                                        <Icon className={`h-4 w-4 ${style.cls}`} />
                                                        <span className="text-gray-700">{style.label}</span>
                                                        {r.errorCount > 1 && (
                                                            <span className="text-xs text-gray-400">×{r.errorCount}</span>
                                                        )}
                                                    </span>
                                                </td>
                                                <td className="px-3 py-2 font-medium text-gray-700">{r.companyCode}</td>
                                                {/* table-fixed clips instead of pushing, so the
                                                    full value stays reachable via title. */}
                                                <td className="truncate px-3 py-2 text-gray-700" title={r.supplier ?? undefined}>
                                                    {r.supplier}
                                                </td>
                                                <td className="truncate px-3 py-2 font-mono text-xs text-gray-700" title={r.invoiceNumber ?? undefined}>
                                                    {r.invoiceNumber}
                                                </td>
                                                <td className="px-3 py-2 text-gray-600">{r.invoiceDate ? formatDate(r.invoiceDate) : '—'}</td>
                                                <td className="px-3 py-2 text-right text-gray-700">
                                                    {r.amount?.toLocaleString('de-CH', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                                    <span className="ml-1 text-xs text-gray-400">{r.currency}</span>
                                                </td>
                                                <td className="px-3 py-2 font-mono text-xs text-gray-600">
                                                    {r.poNo ?? <span className="text-gray-400">{r.poNoRaw ?? '—'}</span>}
                                                </td>
                                                <td className="px-3 py-2 text-gray-600">
                                                    {r.topFindingCode ? (
                                                        <span title={FINDING_LABELS[r.topFindingCode]?.explain}>
                                                            {findingLabel(r.topFindingCode)}
                                                            {r.findingCount > 1 && (
                                                                <span className="ml-1 text-xs text-gray-400">+{r.findingCount - 1}</span>
                                                            )}
                                                        </span>
                                                    ) : <span className="text-gray-300">—</span>}
                                                </td>
                                                <td className="px-3 py-2">
                                                    {r.reviewStatus === 'Open' ? (
                                                        <span className="text-gray-400">Open</span>
                                                    ) : (
                                                        <span className={r.reviewStatus === 'Disputed'
                                                            ? 'text-amber-700' : 'text-green-700'}>
                                                            {r.reviewStatus === 'Disputed' ? 'Disputed' : 'Checked'}
                                                        </span>
                                                    )}
                                                </td>
                                                <td className="px-3 py-2 text-xs text-gray-500">
                                                    {r.checkedAt ? formatDateTimeCH(r.checkedAt) : '—'}
                                                </td>
                                                {/* stopPropagation: opening the invoice must not
                                                    also expand the comparison behind it. */}
                                                <td className="px-3 py-2" onClick={e => e.stopPropagation()}>
                                                    <DocuWareDocLink row={r} onCopyDocId={onCopyDocId} />
                                                </td>
                                            </tr>
                                            {open && (
                                                <tr key={`${r.documentId}-detail`}>
                                                    <td colSpan={COLS.length} className="p-0">
                                                        <PoCheckDetailPanel
                                                            documentId={r.documentId}
                                                            canEdit={canEdit}
                                                            onChanged={() => void reload()}
                                                            onToast={onToast}
                                                            onCopyDocId={onCopyDocId}
                                                        />
                                                    </td>
                                                </tr>
                                            )}
                                        </>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            {total > PO_CHECK_PAGE_SIZE && (
                <div className="flex items-center justify-between text-sm text-gray-600">
                    <span>Page {page} of {totalPages}</span>
                    <div className="flex items-center gap-2">
                        <button
                            onClick={() => setPage(Math.max(1, page - 1))}
                            disabled={page <= 1 || loading}
                            className="inline-flex h-8 items-center gap-1 rounded border border-gray-300 px-2 hover:bg-gray-50 disabled:opacity-40"
                        >
                            <ChevronLeft className="h-4 w-4" /> Previous
                        </button>
                        <button
                            onClick={() => setPage(Math.min(totalPages, page + 1))}
                            disabled={page >= totalPages || loading}
                            className="inline-flex h-8 items-center gap-1 rounded border border-gray-300 px-2 hover:bg-gray-50 disabled:opacity-40"
                        >
                            Next <ChevronRight className="h-4 w-4" />
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
}
